// Client for the Build Guide funnel admin view. Backend: api/core/routers/admin_build_guide.py (platform admins only).

import { ApiError, TokenGetter } from './learningApi';
import { BuildGuideRecommendationKind } from './buildGuideApi';

export type FunnelStepKey = 'started' | 'recommended' | 'clicked' | 'built' | 'ran';
export type BuildKind = 'functions' | 'chatbots' | 'chains' | 'objects';

export interface FunnelStep {
  key: FunnelStepKey;
  label: string;
  count: number;
  /** Share of all conversations. */
  share: number | null;
  /** Share of the previous step's count (steps aren't strictly nested, so this can exceed 1). */
  of_previous: number | null;
}

export interface FunnelDay {
  date: string;
  conversations: number;
  recommended: number;
  clicked: number;
  built: number;
  ran: number;
}

export interface FunnelConversation {
  conversation_id: string;
  customer_id: string;
  customer_tag: string | null;
  started_at: string;
  turns: number;
  kind: BuildGuideRecommendationKind | null;
  targets: string[];
  clicks: number;
  built: boolean;
  built_counts: Record<BuildKind, number>;
  ran: boolean;
  /** Started less than the attribution window ago, so built / ran can still change. */
  open: boolean;
}

export interface BuildGuideFunnel {
  window: { days: number; since: string; until: string; attribution_days: number };
  include_admins: boolean;
  labels: { kinds: Record<string, string>; targets: Record<string, string> };
  totals: { conversations: number; accounts: number; avg_turns: number | null; open_conversations: number };
  steps: FunnelStep[];
  recommendation_kinds: { kind: BuildGuideRecommendationKind; label: string; conversations: number; share: number | null }[];
  click_targets: { target: string; label: string; conversations: number; share: number | null }[];
  daily: FunnelDay[];
  recent: FunnelConversation[];
  truncated: boolean;
}

export function adminBuildGuideApi(getToken: TokenGetter) {
  return {
    funnel: async (days: number, includeAdmins: boolean): Promise<BuildGuideFunnel> => {
      const token = await getToken();
      const params = new URLSearchParams({ days: String(days), include_admins: String(includeAdmins) });
      const res = await fetch(`/api/v2/admin/build-guide/funnel?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let message = res.statusText;
        try {
          const body = await res.json();
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
        } catch {
          /* keep statusText */
        }
        throw new ApiError(message, res.status);
      }
      return res.json();
    },
  };
}
